import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { DownloadDocumentFileType } from '../enums/downloadDocumentFileType'
import { InvoiceDocumentType } from '../enums/invoiceDocumentType'
import { ZervantLocale } from '../enums/zervantLocale'
import { getDocumentDownloadNotificationKey } from '../utils/getDocumentDownloadNotificationKey'
import { getDownloadDocumentFileName } from '../utils/getDownloadDocumentFileName'
import { getDownloadDocumentMimeType } from '../utils/getDownloadDocumentMimeType'
import { useFetchInvoiceDownloadData } from './useFetchInvoiceDownloadData'

interface DownloadInvoiceOptions {
  downloadDocumentFileType: DownloadDocumentFileType
  documentLocale?: ZervantLocale
  documentType: InvoiceDocumentType
  invoiceId: string
  invoiceNumber?: string | null
  onSuccess?: () => void
  onError?: () => void
}

export const useDownloadInvoice = () => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const { fetch: fetchDownloadData, isProcessing } = useFetchInvoiceDownloadData()

  const downloadInvoice = useCallback(
    ({
      downloadDocumentFileType,
      documentLocale,
      documentType,
      invoiceId,
      invoiceNumber,
      onError,
      onSuccess,
    }: DownloadInvoiceOptions) => {
      fetchDownloadData(
        { downloadDocumentFileType, documentType, invoiceId },
        {
          onError: () => {
            notify({
              id: getDocumentDownloadNotificationKey(downloadDocumentFileType),
              message: t('error_saving'),
              variant: 'error',
            })
            onError?.()
          },
          onSuccess: (data) => {
            const fileName = getDownloadDocumentFileName({
              downloadDocumentFileType,
              documentLocale,
              documentType,
              invoiceNumber,
              organizationName: organization?.name,
              t,
            })

            const blob = new Blob([data?.data], { type: getDownloadDocumentMimeType(downloadDocumentFileType) })
            const downloadUrlFromBlob = URL.createObjectURL(blob)

            const downloadLink = document.createElement('a')
            downloadLink.download = fileName
            downloadLink.href = downloadUrlFromBlob

            downloadLink?.click()
            onSuccess?.()
          },
        },
      )
    },
    [fetchDownloadData, organization?.name, t],
  )

  return { download: downloadInvoice, isDownloading: isProcessing }
}
